import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Space, Button, Upload, Input, Select, Divider } from "antd";
import type { InputRef } from "antd/es/input/Input";
import {
  UploadOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import ExcelGuideModal from "./ExcelGuideModal";
import { selectUser } from "../../../Modules/User/Presentation/selectors";
import { useAppSelector } from "../../../Modules/Presentation/hooks";
import {
  isSlotOperationRestricted,
} from "../../../Utils/timeRestriction";
import { getBatchPermissions } from "../../../Utils/slotPermissions";
import { Slot } from "../../../Modules/Slot/Model/Slot";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

const LOCAL_FIND_DEBOUNCE_MS = 180;

interface Props {
  selected: React.Key[];
  selectedSlots: Slot[];
  searchField:
    | "slot"
    | "keyword"
    | "url"
    | "user"
    | "creator"
    | "mid_single"
    | "mid_compare";
  onSearchFieldChange: (v: Props["searchField"]) => void;
  localFindOpen: boolean;
  localFindMatchCount: number;
  localFindActiveMatchIndex: number;
  localFindInputRef: React.RefObject<InputRef>;
  onCloseLocalFind: () => void;
  onLocalFindQueryChange: (value: string) => void;
  onLocalFindNext: () => void;
  onLocalFindPrev: () => void;
  sortBy: "registered" | "start_date" | "end_date";
  sortDir: "asc" | "desc";
  onSortByChange: (v: "registered" | "start_date" | "end_date") => void;
  onSortDirChange: (v: "asc" | "desc") => void;
  onAdd: () => void;
  onClone: () => void;
  onExtend: () => void;
  onEdit: () => void;
  onRefund: () => void;
  onDelete: () => void;
  onSearch: (value: string) => void;
  status: string;
  onStatusChange: (v: string) => void;
  onBatchEdit: () => void;
  onExcelParsedUpload: (file: File) => void;
  onExcelBatchUpdateUpload: (file: File) => void;
  onLegacyExcelDownload: () => void;
  onExcelDownload: () => void;
  excelUploading: boolean;
  legacyExcelDownloading?: boolean;
  excelDownloading?: boolean;
}

const ManagementBar: React.FC<Props> = ({
  selected,
  selectedSlots,
  searchField,
  onSearchFieldChange,
  localFindOpen,
  localFindMatchCount,
  localFindActiveMatchIndex,
  localFindInputRef,
  onCloseLocalFind,
  onLocalFindQueryChange,
  onLocalFindNext,
  onLocalFindPrev,
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirChange,
  onAdd,
  onClone,
  onExtend,
  onEdit,
  onRefund,
  onDelete,
  onSearch,
  onBatchEdit,
  onExcelParsedUpload,
  onExcelBatchUpdateUpload,
  onLegacyExcelDownload,
  onExcelDownload,
  excelUploading,
  legacyExcelDownloading = false,
  excelDownloading = false,
}) => {
  const hasSelection = selected.length > 0;
  const hasSingle = selected.length === 1;
  const [guideOpen, setGuideOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState("");
  const [localFindDraft, setLocalFindDraft] = useState("");
  const localFindTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentUser = useAppSelector(selectUser);
  const navigate = useNavigate();

  // 검색 필드가 바뀌면 입력창만 초기화 (부모 전체 리렌더 유발하지 않음)
  useEffect(() => {
    setSearchDraft("");
  }, [searchField]);

  useEffect(() => {
    if (!localFindOpen) {
      setLocalFindDraft("");
      if (localFindTimerRef.current) {
        clearTimeout(localFindTimerRef.current);
        localFindTimerRef.current = null;
      }
    }
  }, [localFindOpen]);

  useEffect(() => {
    return () => {
      if (localFindTimerRef.current) clearTimeout(localFindTimerRef.current);
    };
  }, []);

  const emitLocalFindQuery = useCallback(
    (value: string, immediate = false) => {
      if (localFindTimerRef.current) {
        clearTimeout(localFindTimerRef.current);
        localFindTimerRef.current = null;
      }

      if (immediate) {
        onLocalFindQueryChange(value);
        return;
      }

      localFindTimerRef.current = setTimeout(() => {
        onLocalFindQueryChange(value);
        localFindTimerRef.current = null;
      }, LOCAL_FIND_DEBOUNCE_MS);
    },
    [onLocalFindQueryChange],
  );

  const handleLocalFindDraftChange = useCallback(
    (value: string) => {
      setLocalFindDraft(value);
      emitLocalFindQuery(value);
    },
    [emitLocalFindQuery],
  );

  const handleCloseLocalFind = useCallback(() => {
    emitLocalFindQuery("", true);
    onCloseLocalFind();
  }, [emitLocalFindQuery, onCloseLocalFind]);

  const isRestricted = currentUser
    ? isSlotOperationRestricted(currentUser)
    : false;
  const isEditRestricted = currentUser
    ? isSlotOperationRestricted(currentUser, "수정")
    : false;

  const batchPermissions = useMemo(
    () => getBatchPermissions(selectedSlots, currentUser, isRestricted),
    [selectedSlots, currentUser, isRestricted],
  );

  const selectedSlot = useMemo(
    () => (hasSingle ? selectedSlots[0] : null),
    [hasSingle, selectedSlots],
  );
  const prefill = useMemo(() => {
    if (!selectedSlot) return null;
    const rawMid = (
      selectedSlot.mid_compare ||
      selectedSlot.mid_single ||
      ""
    ).toString();
    const digits = rawMid.match(/\d+/)?.[0] ?? "";
    return {
      mid: digits,
      main_keyword: selectedSlot.main_keyword || "",
      product_url: selectedSlot.product_url || "",
    };
  }, [selectedSlot]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);


  const selectedHasOptimistic = selectedSlots.some(
    (slot) => slot.is_optimistic,
  );
  const canClone =
    hasSingle &&
    currentUser?.grade !== "SELLER" &&
    !isRestricted &&
    !selectedHasOptimistic;

  const hasLocalFindQuery = localFindDraft.trim().length > 0;
  const localFindStatusText = useMemo(() => {
    if (!localFindOpen || !hasLocalFindQuery) return "입력 대기";
    if (localFindMatchCount === 0) return "결과 없음";
    return `${localFindActiveMatchIndex + 1} / ${localFindMatchCount}`;
  }, [
    hasLocalFindQuery,
    localFindActiveMatchIndex,
    localFindMatchCount,
    localFindOpen,
  ]);

  return (
    <Space
      wrap
      align="center"
      size={isMobile ? "small" : "middle"}
      style={{ padding: isMobile ? 8 : 16, width: "95%", paddingTop: 0 }}
    >
      <Space
        size={isMobile ? "small" : "middle"}
        direction={isMobile ? "vertical" : "horizontal"}
        style={{ width: isMobile ? "100%" : "auto" }}
      >
        <Select
          value={searchField}
          onChange={onSearchFieldChange}
          style={{ width: isMobile ? "100%" : 140 }}
          size="middle"
        >
          <Option value="slot">슬롯 번호</Option>
          <Option value="user">아이디</Option>
          <Option value="creator">생성자</Option>
          <Option value="keyword">키워드</Option>
          <Option value="url">URL</Option>
          <Option value="mid_single">단일 MID</Option>
          <Option value="mid_compare">가격비교 MID</Option>
        </Select>
        {searchField !== "mid_single" && searchField !== "mid_compare" && (
          <Input.Search
            placeholder="검색어 입력"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onSearch={onSearch}
            allowClear
            enterButton="검색"
            style={{ width: isMobile ? "100%" : 250 }}
            size="middle"
          />
        )}
        {localFindOpen && (
          <Space
            className="slot-local-find"
            size={isMobile ? "small" : "middle"}
            direction={isMobile ? "vertical" : "horizontal"}
            style={{ width: isMobile ? "100%" : "auto" }}
          >
            <Input
              ref={localFindInputRef}
              placeholder="현재 페이지에서 찾기"
              value={localFindDraft}
              onChange={(e) => handleLocalFindDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  handleCloseLocalFind();
                  return;
                }

                if (e.key === "Enter") {
                  e.preventDefault();
                  emitLocalFindQuery(localFindDraft, true);
                  if (e.shiftKey) onLocalFindPrev();
                  else onLocalFindNext();
                }
              }}
              allowClear
              onClear={() => handleLocalFindDraftChange("")}
              prefix={<SearchOutlined />}
              style={{ width: isMobile ? "100%" : 240 }}
              size="middle"
            />
            <span
              style={{
                color: "#666",
                minWidth: isMobile ? undefined : 72,
                textAlign: "center",
              }}
            >
              {localFindStatusText}
            </span>
            <Space size="small">
              <Button
                onClick={onLocalFindPrev}
                disabled={!hasLocalFindQuery || localFindMatchCount === 0}
                size={isMobile ? "small" : "middle"}
              >
                이전
              </Button>
              <Button
                onClick={onLocalFindNext}
                disabled={!hasLocalFindQuery || localFindMatchCount === 0}
                size={isMobile ? "small" : "middle"}
              >
                다음
              </Button>
              <Button
                onClick={handleCloseLocalFind}
                size={isMobile ? "small" : "middle"}
              >
                닫기
              </Button>
            </Space>
          </Space>
        )}
      </Space>

      {(searchField === "mid_single" || searchField === "mid_compare") && (
        <Space
          size={isMobile ? "small" : "middle"}
          direction={isMobile ? "vertical" : "horizontal"}
          style={{ width: isMobile ? "100%" : "auto" }}
        >
          <Input.Search
            placeholder={`${searchField === "mid_single" ? "단일" : "가격비교"} MID 입력`}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onSearch={onSearch}
            allowClear
            enterButton="검색"
            style={{ width: isMobile ? "100%" : 250 }}
            size="middle"
          />
        </Space>
      )}

      {!isMobile && <Divider type="vertical" />}

      <Space
        size={isMobile ? "small" : "middle"}
        direction={isMobile ? "vertical" : "horizontal"}
      >
        <Select
          value={sortBy}
          onChange={onSortByChange}
          style={{ width: isMobile ? "100%" : 140 }}
          size="middle"
        >
          <Option value="registered">등록순</Option>
          <Option value="start_date">시작일</Option>
          <Option value="end_date">종료일</Option>
        </Select>
        <Select
          value={sortDir}
          onChange={onSortDirChange}
          style={{ width: isMobile ? "100%" : 110 }}
          size="middle"
        >
          <Option value="desc">내림차순</Option>
          <Option value="asc">오름차순</Option>
        </Select>
      </Space>

      {!isMobile && <Divider type="vertical" />}

      <Space size="small" wrap>
        <Button
            onClick={onExtend}
            disabled={!hasSelection || !batchPermissions.canExtend}
            size={isMobile ? "small" : "middle"}
          >
            연장
          </Button>
        <Button
            onClick={onEdit}
            disabled={!hasSingle || !batchPermissions.canEdit}
            size={isMobile ? "small" : "middle"}
          >
            수정
          </Button>
        <Button
            onClick={onBatchEdit}
            disabled={!hasSelection || !batchPermissions.canBatchEdit}
            size={isMobile ? "small" : "middle"}
          >
            일괄 수정
          </Button>
        <Button
            danger
            onClick={onRefund}
            disabled={!hasSelection || !batchPermissions.canRefund}
            size={isMobile ? "small" : "middle"}
          >
            환불
          </Button>
        {currentUser?.grade !== "SELLER" && (
          <Button
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
              disabled={!hasSelection || !batchPermissions.canDelete}
              size={isMobile ? "small" : "middle"}
            >
              삭제
            </Button>
        )}
      </Space>

      {!isMobile && <Divider type="vertical" />}

      {currentUser?.grade !== "SELLER" && (
        <>
          {!isMobile && <Divider type="vertical" />}
          <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAdd}
              disabled={isRestricted}
              size={isMobile ? "small" : "middle"}
            >
              슬롯 추가
            </Button>
          <Button
              icon={<CopyOutlined />}
              onClick={onClone}
              disabled={!canClone}
              size={isMobile ? "small" : "middle"}
            >
              복제
            </Button>
        </>
      )}

      <Space size="small" wrap>
        <Button
          onClick={() => setGuideOpen(true)}
          size={isMobile ? "small" : "middle"}
        >
          엑셀 가이드
        </Button>
        {currentUser?.grade !== "SELLER" && (
          <span style={{ display: "inline-block" }}>
              <Upload
                beforeUpload={(file) => {
                  onExcelParsedUpload(file);
                  return false;
                }}
                showUploadList={false}
                fileList={[]}
                disabled={isRestricted}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={excelUploading}
                  disabled={isRestricted}
                  size={isMobile ? "small" : "middle"}
                >
                  엑셀 업로드
                </Button>
              </Upload>
            </span>
        )}
        {currentUser?.grade !== "SELLER" && (
          <span style={{ display: "inline-block" }}>
              <Upload
                beforeUpload={(file) => {
                  onExcelBatchUpdateUpload(file);
                  return false;
                }}
                showUploadList={false}
                fileList={[]}
                disabled={isEditRestricted}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={excelUploading}
                  disabled={isEditRestricted}
                  size={isMobile ? "small" : "middle"}
                >
                  엑셀 일괄 수정
                </Button>
              </Upload>
            </span>
        )}
        <Button
          icon={<DownloadOutlined />}
          onClick={onLegacyExcelDownload}
          loading={legacyExcelDownloading}
          size={isMobile ? "small" : "middle"}
        >
          엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
        </Button>
        <Button
          icon={<DownloadOutlined />}
          onClick={onExcelDownload}
          loading={excelDownloading}
          size={isMobile ? "small" : "middle"}
        >
          (상품명추출)엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
        </Button>
        {currentUser?.grade === "ADMIN" && (
          <Button
              type="default"
              icon={<SearchOutlined />}
              onClick={() => {
                if (!hasSingle || !prefill) return;
                navigate("/keyword-optimization", {
                  state: { keywordPrefill: prefill },
                });
              }}
              disabled={!hasSingle || !prefill}
              size={isMobile ? "small" : "middle"}
            >
              키워드별 순위
            </Button>
        )}
      </Space>

      <ExcelGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Space>
  );
};

export default React.memo(ManagementBar);
